import React, { useCallback, useRef, useState } from 'react';
import { Plus, X, Play, Loader2, Download } from 'lucide-react';
import {
  ANN_VOL_FUNC_OPTIONS,
  BacktestResponse,
  DEFAULT_FIT_SPEC,
  DEFAULT_STRATEGY_DRAFT,
  emptySignal,
  FitSpec,
  MIXING_OBJECTIVE_OPTIONS,
  runStrategySystematics,
  SEL_RULE_OPTIONS,
  SIGNAL_FUNC_OPTIONS,
  SignalDraft,
  SLICE_TYPE_OPTIONS,
  StrategyConfig,
  StrategyDraft,
  toStrategyConfig,
} from '../services/StrategySystematicsService';
import { GridRangeRows } from '../components/strategy-systematics/GridRangeRows';
import { NumberListEditor } from '../components/strategy-systematics/NumberListEditor';
import {
  CheckCell,
  DateCell,
  IconButton,
  NumberCell,
  ParamNest,
  ParamRow,
  ParamSection,
  ParamSheet,
  SelectCell,
  SheetButton,
  TenorCell,
  TextCell,
} from '../components/strategy-systematics/ParamSheet';
import { StrategyPnlChart } from '../components/strategy-systematics/StrategyPnlChart';
import { downloadSystematicsSeries } from '../components/strategy-systematics/strategySeriesExport';
import { SideNavigationMenu } from '../components/library/side-navigation-menu/SideNavigationMenu';
import styles from './StrategySystematicsPage.module.css';

const fmt = (v: number | null | undefined, digits = 3): string =>
  v === null || v === undefined || !Number.isFinite(v) ? '—' : v.toFixed(digits);

const StrategySystematicsPage: React.FC = () => {
  const [config, setConfig] = useState<StrategyDraft>(DEFAULT_STRATEGY_DRAFT);
  const [fitSpec, setFitSpec] = useState<FitSpec>(DEFAULT_FIT_SPEC);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BacktestResponse | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const patch = useCallback((p: Partial<StrategyDraft>) => setConfig((c) => ({ ...c, ...p })), []);
  const patchFit = useCallback((p: Partial<FitSpec>) => setFitSpec((f) => ({ ...f, ...p })), []);

  const updateSignal = (idx: number, p: Partial<SignalDraft>) =>
    setConfig((c) => ({ ...c, signals: c.signals.map((s, i) => (i === idx ? { ...s, ...p } : s)) }));
  const removeSignal = (idx: number) =>
    setConfig((c) => ({ ...c, signals: c.signals.filter((_, i) => i !== idx) }));
  const addSignal = () => setConfig((c) => ({ ...c, signals: [...c.signals, emptySignal()] }));

  const onRun = async () => {
    let payload: StrategyConfig;
    try {
      payload = toStrategyConfig(config);
    } catch (e) {
      setError((e as Error).message);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setRunning(true);
    setError(null);
    try {
      const res = await runStrategySystematics(payload, fitSpec, controller.signal);
      setResult(res);
    } catch (e) {
      if ((e as Error).name !== 'AbortError') {
        setError((e as Error).message || 'Backtest failed');
      }
    } finally {
      setRunning(false);
    }
  };

  const resetDefaults = () => {
    setConfig(DEFAULT_STRATEGY_DRAFT);
    setFitSpec(DEFAULT_FIT_SPEC);
    setError(null);
  };

  const totalParamSignals = config.signals.reduce((n, s) => n + s.params.length, 0);
  const sliced = fitSpec.slice_type !== 'full_sample';

  return (
    <div className={styles.page}>
      <SideNavigationMenu isOpen={true} isCollapsed={true} lockCollapsed={true} />
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Strategy Systematic Backtest</h1>
          <p className={styles.subtitle}>
            Configure a strategy, add signals, and run the systematic backtester.
          </p>
        </div>
        <div className={styles.headerActions}>
          <button type="button" className={styles.secondaryBtn} onClick={resetDefaults} disabled={running}>
            Reset form
          </button>
          <button type="button" className={styles.runBtn} onClick={onRun} disabled={running || config.signals.length === 0}>
            {running ? <Loader2 size={16} className={styles.spin} /> : <Play size={16} />}
            {running ? 'Running…' : 'Run Backtest'}
          </button>
        </div>
      </header>

      {error && <div className={styles.errorBar}>{error}</div>}

      <div className={styles.body}>
        {/* ---------------- Config panel ---------------- */}
        <section className={styles.configPanel}>
          <ParamSheet>
            <ParamSection title="Basics">
              <ParamRow label="Strategy name">
                <TextCell value={config.strategy_name} onChange={(v) => patch({ strategy_name: v })} />
              </ParamRow>
              <ParamRow label="PnL ticker" hint="pnl_ts">
                <TextCell value={config.pnl_ts} onChange={(v) => patch({ pnl_ts: v })} />
              </ParamRow>
              <ParamRow label="Start date">
                <DateCell value={config.start_date} onChange={(v) => patch({ start_date: v })} />
              </ParamRow>
              <ParamRow label="End date">
                <DateCell value={config.end_date} onChange={(v) => patch({ end_date: v })} />
              </ParamRow>
              <ParamRow label="AUM">
                <NumberCell value={config.aum} onChange={(v) => patch({ aum: v })} />
              </ParamRow>
            </ParamSection>

            <ParamSection title="Signals" badge={`${config.signals.length} families · ${totalParamSignals} params`}>
              {config.signals.map((sig, idx) => (
                <ParamSection
                  key={idx}
                  title={sig.name.trim() || `Signal ${idx + 1}`}
                  badge={sig.base_ts.trim() || undefined}
                  actions={
                    <IconButton label="Remove signal" onClick={() => removeSignal(idx)}>
                      <X size={14} />
                    </IconButton>
                  }
                >
                  <ParamNest>
                    <ParamRow label="Signal name">
                      <TextCell value={sig.name} placeholder="name"
                        onChange={(v) => updateSignal(idx, { name: v })} />
                    </ParamRow>
                    <ParamRow label="Base ticker" hint="base_ts">
                      <TextCell value={sig.base_ts} placeholder="ticker"
                        onChange={(v) => updateSignal(idx, { base_ts: v })} />
                    </ParamRow>
                    <ParamRow label="Transform">
                      <SelectCell value={sig.func} options={SIGNAL_FUNC_OPTIONS} placeholder="Select transform…"
                        onChange={(v) => updateSignal(idx, { func: v })} />
                    </ParamRow>
                    <ParamRow label="Window params" hint="lookbacks in days">
                      <NumberListEditor integer values={sig.params}
                        onChange={(params) => updateSignal(idx, { params })} addLabel="param" />
                    </ParamRow>
                    <ParamRow label="Selection rule">
                      <SelectCell value={sig.sel_rule} options={SEL_RULE_OPTIONS} placeholder="Select rule…"
                        onChange={(v) => updateSignal(idx, { sel_rule: v })} />
                    </ParamRow>
                    <ParamRow label="Cap signal weight">
                      <CheckCell checked={sig.wt_cap !== null} onLabel="Capped" offLabel="Uncapped"
                        onChange={(on) => updateSignal(idx, { wt_cap: on ? 1 : null })} />
                    </ParamRow>
                    {sig.wt_cap !== null && (
                      <ParamRow label="Weight cap" hint="wt_cap">
                        <NumberCell value={sig.wt_cap} onChange={(v) => updateSignal(idx, { wt_cap: v })} />
                      </ParamRow>
                    )}
                    <ParamRow label="Optimize transaction costs" hint="tc_opt">
                      <CheckCell checked={sig.tc_opt} onChange={(v) => updateSignal(idx, { tc_opt: v })} />
                    </ParamRow>
                  </ParamNest>
                </ParamSection>
              ))}
              <SheetButton onClick={addSignal}>
                <Plus size={14} /> Add signal
              </SheetButton>
            </ParamSection>

            <ParamSection title="Fit / OOS slicing" defaultOpen={false}>
              <ParamRow label="Slice type">
                <SelectCell value={fitSpec.slice_type} options={SLICE_TYPE_OPTIONS}
                  onChange={(v) => patchFit({ slice_type: v })} />
              </ParamRow>
              {sliced && (
                <ParamNest>
                  <ParamRow label="Fit window">
                    <TenorCell value={fitSpec.fit_window} onChange={(v) => patchFit({ fit_window: v })} />
                  </ParamRow>
                  <ParamRow label="OOS window">
                    <TenorCell value={fitSpec.oos_window} onChange={(v) => patchFit({ oos_window: v })} />
                  </ParamRow>
                  <ParamRow label="Pin OOS start">
                    <CheckCell checked={fitSpec.oos_start_anchor !== null} onLabel="Pinned" offLabel="Auto"
                      onChange={(on) => patchFit({ oos_start_anchor: on ? config.start_date : null })} />
                  </ParamRow>
                  {fitSpec.oos_start_anchor !== null && (
                    <ParamRow label="OOS start anchor">
                      <DateCell value={fitSpec.oos_start_anchor}
                        onChange={(v) => patchFit({ oos_start_anchor: v })} />
                    </ParamRow>
                  )}
                </ParamNest>
              )}
            </ParamSection>

            <ParamSection title="Optimizer" defaultOpen={false}>
              <ParamRow label="Mixing objective">
                <SelectCell value={config.mixing_objective} options={MIXING_OBJECTIVE_OPTIONS}
                  onChange={(v) => patch({ mixing_objective: v })} />
              </ParamRow>
              <ParamRow label="SR improvement threshold">
                <NumberCell value={config.sr_improv_thresh} onChange={(v) => patch({ sr_improv_thresh: v })} />
              </ParamRow>
              <ParamRow label="Correlation shrinkage">
                <NumberCell value={config.corr_shrinkage} onChange={(v) => patch({ corr_shrinkage: v })} />
              </ParamRow>
              <GridRangeRows label="TC aversion grid" hint="tca_vec" range={config.tca_range}
                onChange={(tca_range) => patch({ tca_range })} />
              <GridRangeRows label="Risk aversion grid" hint="ra_vec" range={config.ra_range}
                onChange={(ra_range) => patch({ ra_range })} />
              <ParamRow label="Non mean-variance run" hint="non_mv_run">
                <CheckCell checked={config.non_mv_run} onChange={(v) => patch({ non_mv_run: v })} />
              </ParamRow>
            </ParamSection>

            <ParamSection title="Risk & sizing" defaultOpen={false}>
              <ParamRow label="Vol target">
                <NumberCell value={config.vol_target} onChange={(v) => patch({ vol_target: v })} />
              </ParamRow>
              <ParamRow label="Upper risk limit">
                <NumberCell value={config.upper_risk_limit} onChange={(v) => patch({ upper_risk_limit: v })} />
              </ParamRow>
              <ParamRow label="Cap position size">
                <CheckCell checked={config.upper_position_limit !== null} onLabel="Capped" offLabel="Uncapped"
                  onChange={(on) => patch({ upper_position_limit: on ? 1 : null })} />
              </ParamRow>
              {config.upper_position_limit !== null && (
                <ParamNest>
                  <ParamRow label="Upper position limit">
                    <NumberCell value={config.upper_position_limit}
                      onChange={(v) => patch({ upper_position_limit: v })} />
                  </ParamRow>
                </ParamNest>
              )}
              <ParamRow label="Filter by vol">
                <CheckCell checked={config.filter_by_vol} onChange={(v) => patch({ filter_by_vol: v })} />
              </ParamRow>
              <ParamRow label="Scale signals to asset vol">
                <CheckCell checked={config.scale_signals_to_asset_vol}
                  onChange={(v) => patch({ scale_signals_to_asset_vol: v })} />
              </ParamRow>
              <ParamRow label="Vol trigger signal">
                <CheckCell checked={config.vol_trigger_signal} onChange={(v) => patch({ vol_trigger_signal: v })} />
              </ParamRow>
              {config.vol_trigger_signal && (
                <ParamNest>
                  <ParamRow label="Vol trigger level">
                    <NumberCell value={config.vol_trigger_level} onChange={(v) => patch({ vol_trigger_level: v })} />
                  </ParamRow>
                </ParamNest>
              )}
            </ParamSection>

            <ParamSection title="Advanced" defaultOpen={false}>
              <ParamRow label="Volatility function" hint="ann_vol_func">
                <SelectCell value={config.ann_vol_func} options={ANN_VOL_FUNC_OPTIONS}
                  onChange={(v) => patch({ ann_vol_func: v })} />
              </ParamRow>
              <ParamRow label="TC linear intercept" hint="tc_lin_int">
                <NumberCell value={config.tc_lin_int} onChange={(v) => patch({ tc_lin_int: v })} />
              </ParamRow>
              <ParamRow label="TC linear slope" hint="tc_lin_slope">
                <NumberCell value={config.tc_lin_slope} onChange={(v) => patch({ tc_lin_slope: v })} />
              </ParamRow>
              <ParamRow label="TC quadratic" hint="tc_quad">
                <NumberCell value={config.tc_quad} onChange={(v) => patch({ tc_quad: v })} />
              </ParamRow>
            </ParamSection>
          </ParamSheet>
        </section>

        {/* ---------------- Results panel ---------------- */}
        <section className={styles.resultsPanel}>
          {!result && !running && (
            <div className={styles.placeholder}>Configure a strategy and click <strong>Run Backtest</strong> to see results.</div>
          )}
          {running && <div className={styles.placeholder}><Loader2 size={20} className={styles.spin} /> Running backtest…</div>}

          {result && (
            <>
              <div className={styles.card}>
                <div className={styles.cardTitleRow}>
                  <h3 className={styles.cardTitle}>Cumulative OOS PnL</h3>
                  <button type="button" className={styles.secondaryBtn}
                    onClick={() => downloadSystematicsSeries(result, config.strategy_name, config.aum)}>
                    <Download size={15} /> Download series (Excel)
                  </button>
                </div>
                <StrategyPnlChart dates={result.dates} cumPnl={result.cum_pnl} />
              </div>

              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Performance</h3>
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Period</th><th>Begin</th><th>End</th><th>Sharpe</th>
                        <th>Ann vol</th><th>Max DD</th><th>Skew</th><th>Kurtosis</th><th>CR 5p</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.perf.map((r, i) => (
                        <tr key={i} className={i === 0 ? styles.fsRow : undefined}>
                          <td>{r.label === 'full_sample' ? 'Full sample' : r.label}</td>
                          <td>{r.begin}</td><td>{r.end}</td>
                          <td>{fmt(r.sharpe_ratio)}</td>
                          <td>{fmt(r.ann_vol, 4)}</td>
                          <td>{fmt(r.max_dd, 4)}</td>
                          <td>{fmt(r.skew)}</td>
                          <td>{fmt(r.kurtosis)}</td>
                          <td>{fmt(r.cr_ratio_5p)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Selected signals per slice</h3>
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Fit start</th><th>Fit end</th><th>OOS start</th><th>OOS end</th>
                        <th>Signal</th><th className={styles.num}>Loading</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.slices.map((s, i) => {
                        const rows = s.loadings.length === 0 ? [null] : s.loadings;
                        return rows.map((entry, j) => (
                          <tr key={`${i}-${j}`} className={j === 0 ? styles.sliceStart : undefined}>
                            {j === 0 && (
                              <>
                                <td rowSpan={rows.length} className={styles.sliceCell}>{s.fit_start}</td>
                                <td rowSpan={rows.length} className={styles.sliceCell}>{s.fit_end}</td>
                                <td rowSpan={rows.length} className={styles.sliceCell}>{s.oos_start}</td>
                                <td rowSpan={rows.length} className={styles.sliceCell}>{s.oos_end}</td>
                              </>
                            )}
                            {entry === null ? (
                              <td colSpan={2} className={styles.muted}>no signals selected</td>
                            ) : (
                              <>
                                <td className={styles.sigName}>{entry[0]}</td>
                                <td className={`${styles.num} ${entry[1] < 0 ? styles.neg : styles.pos}`}>
                                  {entry[1].toFixed(3)}
                                </td>
                              </>
                            )}
                          </tr>
                        ));
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default StrategySystematicsPage;
